import { FC, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UploadArea } from '@nx-frontend/shared/ui/upload-area';
import { ChatbotUserUploadedFile, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { AIAgentInputField } from '@nx-frontend/shared/types/ai-agents';
import { humanReadableFileSize } from '@nx-frontend/shared/utils/file-size';

interface ChosenFileProps {
  name: string;
  size?: number;
  clearFileUpload: () => void;
}

const ChosenFile: FC<ChosenFileProps> = ({ name, size, clearFileUpload }) => {
  return (
    <div className="flex flex-row items-center justify-center p-5">
      <div>
        {name} {size && humanReadableFileSize(size)}{' '}
      </div>
      <Icon
        onClick={clearFileUpload}
        className="w-4 h-4 ml-5 mt-[-1px] text-neutral-500 cursor-pointer"
        icon={IconsMap.TRASH}
      />
    </div>
  );
};

export const AgentInputFile: FC<{
  field: AIAgentInputField;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onChange: (value: string | File) => void;
  showError?: boolean;
  errorText?: string;
  defaultValue?: ChatbotUserUploadedFile;
  cancelDefaultValue?: () => void;
}> = ({
  field,
  themeColors,
  themeStyle,
  onChange,
  showError,
  errorText,
  defaultValue,
  cancelDefaultValue = () => {
    /* */
  },
}) => {
  const [chosenFile, setChosenFile] = useState<File>();

  const onFilesSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      setChosenFile(files[0]);
      onChange(files[0]);
    }
  };

  const clearFileUpload = () => {
    setChosenFile(undefined);
    onChange('');
  };

  let fileComponent: JSX.Element | null = null;
  if (chosenFile) {
    fileComponent = <ChosenFile name={chosenFile.name} size={chosenFile.size} clearFileUpload={clearFileUpload} />;
  } else if (defaultValue) {
    fileComponent = (
      <ChosenFile name={defaultValue.filename} size={defaultValue.size} clearFileUpload={cancelDefaultValue} />
    );
  }

  return (
    <div className="flex flex-col mt-4">
      <label
        className={classNames('text-lg font-semibold', {
          'text-neutral-800': !showError,
          'text-red-500': showError,
        })}
      >
        {field.label}
        {field.required && <span className="text-red-500">*</span>}
      </label>
      {field.description && <div className="text-[16px] text-neutral-500 mt-2">{field.description}</div>}

      {fileComponent ? (
        fileComponent
      ) : (
        <div className="bg-teal-50 rounded-xl mt-4">
          <UploadArea
            borderThin={true}
            extendedView={true}
            onFilesSelect={onFilesSelect}
            themeColors={themeColors}
            themeStyle={themeStyle}
          />
        </div>
      )}
      {showError && <div className="text-red-500 text-xs mt-2">{errorText}</div>}
    </div>
  );
};
